import { motion } from "framer-motion";
import { Check, ArrowUpRight, Presentation } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToSection } from "../../config/site";

const FEATURES = [
  "Student Management", "Teacher & Staff Management", "Admission Management", "Attendance Management",
  "Fees & Online Payment", "Examination & Results", "Homework & Assignments", "Parent Portal",
  "Teacher Portal", "Student Portal", "School Website", "Custom Domain", "Mobile App",
  "SMS & Notifications", "Online Examination", "Reports & Analytics", "Certificates & ID Cards",
  "Library Management", "Transport Management", "Payroll", "AI-powered features",
];

export const SchoolSaaS = () => (
  <section id="school-saas" className="grain grid-lines-dark relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="school-saas-section">
    <div className="pointer-events-none absolute left-[-10%] top-[10%] h-[460px] w-[460px] rounded-full bg-gold/[0.09] blur-[130px]" />
    <div className="pointer-events-none absolute bottom-[-10%] right-[-6%] h-[420px] w-[420px] rounded-full bg-magenta/[0.08] blur-[130px]" />

    <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
        <div>
          <Reveal>
            <Eyebrow dark>Featured Solution</Eyebrow>
            <h2 className="mt-5 font-jakarta text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="school-saas-heading">
              Custom School Management SaaS
            </h2>
            <p className="mt-3 font-jakarta text-lg font-semibold">
              <span className="gradient-brand">Your School. Your Branding. Your Digital Platform.</span>
            </p>
            <p className="mt-5 max-w-lg font-inter text-[15px] leading-relaxed text-slate-400">
              RCS provides customized School Management SaaS solutions designed specifically for schools and
              educational institutions. Manage students, teachers, attendance, fees, examinations, communication,
              parents and academic operations from one powerful digital platform.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <ul className="mt-8 flex flex-wrap gap-2" data-testid="school-saas-features">
              {FEATURES.map((f, i) => (
                <motion.li
                  key={f}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.03 * i, duration: 0.4 }}
                  className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 font-inter text-[11.5px] text-slate-300 transition-colors duration-300 hover:border-gold/50 hover:text-white"
                >
                  <Check className="h-3 w-3 text-gold-light" />
                  {f}
                </motion.li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap gap-4">
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("rcs:prefill-service", { detail: "School Management SaaS" }));
                  scrollToSection("#contact");
                }}
                data-testid="school-saas-build-cta"
                className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-4 font-jakarta text-sm font-semibold text-navy transition-colors duration-300 hover:bg-gold-light"
              >
                Build Your School SaaS
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("rcs:prefill-service", { detail: "School Management SaaS" }));
                  scrollToSection("#contact");
                }}
                data-testid="school-saas-demo-cta"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/20 px-7 py-4 font-jakarta text-sm font-semibold text-white transition-colors duration-300 hover:border-magenta hover:text-magenta"
              >
                <Presentation className="h-4 w-4" />
                Book a Free Demo
              </button>
            </div>
            <p className="mt-6 border-l-2 border-gold pl-4 font-inter text-[13px] italic text-slate-400" data-testid="school-saas-highlight">
              School-specific branding, workflows, features and access control.
            </p>
          </Reveal>
        </div>

        {/* product visual */}
        <Reveal delay={0.2} className="relative">
          <div className="perspective-1200 relative" data-testid="school-saas-mockup">
            <motion.div
              initial={{ rotateY: -10, rotateX: 6 }}
              whileInView={{ rotateY: 0, rotateX: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-xl border border-white/10 bg-ink shadow-[0_50px_120px_-30px_rgba(0,0,0,0.8)]"
            >
              <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-magenta/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-gold/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
                <span className="ml-3 font-mono2 text-[10px] tracking-widest text-slate-500">YOURSCHOOL.EDU · BRANDED PORTAL</span>
              </div>
              <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
                {["Admissions", "Attendance", "Fees", "Exams", "Homework", "Transport", "Library", "Payroll"].map((m, i) => (
                  <div key={m} className={`rounded-lg border p-3 ${i === 1 ? "border-gold/40 bg-gold/10" : "border-white/10 bg-white/[0.04]"}`}>
                    <div className={`h-1.5 w-8 rounded ${i === 1 ? "bg-gold" : "bg-white/15"}`} />
                    <p className="mt-2.5 font-jakarta text-[11px] font-semibold text-slate-200">{m}</p>
                    <p className="font-mono2 text-[8px] uppercase tracking-widest text-slate-500">Module</p>
                  </div>
                ))}
              </div>
              <div className="mx-4 mb-4 rounded-lg border border-white/10 bg-white/[0.04] p-3">
                <div className="flex items-center justify-between">
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">Role-based access</p>
                  <div className="flex gap-1.5">
                    {["Admin", "Teacher", "Parent", "Student"].map((r) => (
                      <span key={r} className="rounded-full border border-magenta/30 bg-magenta/10 px-2 py-0.5 font-mono2 text-[8px] text-magenta">{r}</span>
                    ))}
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {[88, 64, 76].map((w, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 rounded bg-white/10">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${w}%` }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.4 + i * 0.15, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                          className={`h-full rounded ${i === 0 ? "bg-gold/80" : i === 1 ? "bg-magenta/70" : "bg-emerald-400/70"}`}
                        />
                      </div>
                      <span className="font-mono2 text-[8px] text-slate-500">{w}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24, rotate: 5 }}
              whileInView={{ opacity: 1, y: 0, rotate: 5 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-8 -right-2 w-32 rounded-xl border border-white/10 bg-ink/95 p-3 shadow-2xl backdrop-blur float-y sm:-right-6 sm:w-36"
            >
              <p className="font-mono2 text-[8px] uppercase tracking-[0.2em] text-slate-500">School app</p>
              <p className="mt-1.5 font-jakarta text-[12px] font-bold text-white">Result published</p>
              <p className="font-mono2 text-[8px] text-slate-500">Grade 10 · Term II</p>
              <div className="mt-2.5 rounded-md bg-gold/15 px-2 py-1.5 font-mono2 text-[8px] text-gold-light">SMS + Push sent to 412 parents</div>
            </motion.div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
